/**
 * Copyright 2026 Ziz Academy
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { submissionApi } from '../services/api';
import { ExamSubmission } from '../types';

// Helper to render ordinal numbers (1st, 2nd, 3rd, 4th...)
const ordinal = (n: number | undefined) => {
    if (!n) return '';
    const s = ["th", "st", "nd", "rd"], v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const ExamResult: React.FC = () => {
    const { submissionId } = useParams<{ submissionId: string }>();
    const [submission, setSubmission] = useState<ExamSubmission | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSubmission = async () => {
            try {
                const data = await submissionApi.getById(Number(submissionId));
                setSubmission(data);
            } catch (error) {
                console.error('Failed to fetch submission:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSubmission();
    }, [submissionId]);

    const getScoreEmoji = (percentage: number) => {
        if (percentage >= 90) return '🏆';
        if (percentage >= 75) return '🌟';
        if (percentage >= 50) return '👍';
        return '💪';
    };

    const getScoreMessage = (percentage: number) => {
        if (percentage >= 90) return 'Amazing! You\'re a superstar!';
        if (percentage >= 75) return 'Great job! Keep it up!';
        if (percentage >= 50) return 'Good effort! You can do better!';
        return 'Keep practicing! You\'ll get better!';
    };

    if (loading) {
        return (
            <div className="loading" data-testid="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!submission) {
        return (
            <div className="empty-state">
                <div className="empty-state-emoji">😕</div>
                <h3 className="empty-state-title">Result Not Found</h3>
            </div>
        );
    }

    const percentage = Math.round(
        ((submission.totalScore || 0) / (submission.totalMarks || 1)) * 100
    );

    const hasPending = (submission.studentAnswers || []).some(a => !a.isEvaluated);

    // Compute MCQ total marks from answers identified as MCQ (selectedAnswerId is present)
    const mcqAnswers = (submission.studentAnswers || []).filter(a => a.selectedAnswerId != null);
    const mcqTotalMarks = mcqAnswers.reduce((sum, a) => sum + (a.maxMarks || 0), 0);

    return (
        <div data-testid="exam-result">
            {/* Score Header */}
            <div className="score-display animate-pulse">
                <div className="score-emoji">{hasPending ? '⌛' : getScoreEmoji(percentage)}</div>
                <div className="score-number">
                    {hasPending ? 'Evaluation Pending' : `${submission.totalScore} / ${submission.totalMarks}`}
                </div>
                <div className="score-label">{hasPending ? "Some subjective answers need to be evaluated by your teacher." : getScoreMessage(percentage)}</div>
            </div>

            {/* Exam Info */}
            <div className="card">
                <h2 style={{ marginBottom: '1rem' }}>
                    {submission.examTitle}
                    {submission.attemptNumber && submission.attemptNumber > 1 && (
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.95rem', color: '#718096' }}>
                            ({ordinal(submission.attemptNumber)} Attempt)
                        </span>
                    )}
                </h2>
                <div className="exam-info">
                    <div className="exam-info-item">
                        <span className="icon">📊</span>
                        <span>MCQ Marks: {submission.mcqScore || 0} / {mcqTotalMarks}</span>
                    </div>
                    <div className="exam-info-item">
                        <span className="icon">✍</span>
                        <span>
                            Subjective Marks: {hasPending ? 'Evaluation Pending' : (submission.subjectiveScore || 0)}
                        </span>
                    </div>
                    <div className="exam-info-item">
                        <span className="icon">📅</span>
                        <span>
                            Submitted: {' '}
                            {submission.submittedAt
                                ? new Date(submission.submittedAt).toLocaleString()
                                : 'N/A'}
                        </span>
                    </div>
                </div>
                {!submission.isEvaluated && (
                    <div className="alert alert-success" style={{ marginTop: '1rem' }}>
                        ⌛ Some subjective answers are still being evaluated by your teacher.
                    </div>
                )}
            </div>

            {/* Answers Review */}
            <h3 style={{ marginBottom: '1rem' }}>📝 Your answers</h3>
            {submission.studentAnswers?.map((answer, index) => (
                <div
                    key={answer.id}
                    className="question-card"
                    style={{
                        borderColor: answer.isCorrect === true || (answer.marksAwarded && answer.marksAwarded > 0)
                            ? '#38ef7d'
                            : answer.isCorrect === false || (answer.isEvaluated && answer.marksAwarded === 0)
                                ? '#f45c43'
                                : '#667eea',
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                            <span className="question-number">{index + 1}.</span>
                            {answer.questionRichText && answer.questionRichText.trim().length > 0 ? (
                                <div
                                    className="question-text"
                                    style={{ display: 'inline-block', marginLeft: '0.5rem', lineHeight: 1.5 }}
                                    dangerouslySetInnerHTML={{ __html: answer.questionRichText }}
                                />
                            ) : (
                                <span className="question-text" style={{ marginLeft: '0.5rem' }}>{answer.questionText}</span>
                            )}
                            {answer.questionImagePath && (
                                <div style={{ marginTop: '0.5rem' }}>
                                    <img
                                        src={answer.questionImagePath}
                                        alt="Question"
                                        style={{ maxWidth: '100%', maxHeight: '380px', borderRadius: '10px', border: '1px solid #e2e8f0' }}
                                    />
                                </div>
                            )}
                        </div>
                        <span className="questionmarks">
                            {!answer.isEvaluated ? 'Evaluation Pending' : `${answer.marksAwarded ?? 0}/${answer.maxMarks}`}
                        </span>
                    </div>

                    {answer.selectedAnswerText && (
                        <div
                            className={`answer-option ${answer.isCorrect ? 'correct' : 'incorrect'}`}
                            style={{ marginTop: '1rem', cursor: 'default' }}
                        >
                            <span>Your answer: {answer.selectedAnswerText}</span>
                            {answer.isCorrect ? (
                                <span style={{ marginLeft: 'auto' }}>✅</span>
                            ) : (
                                <span style={{ marginLeft: 'auto' }}>❌</span>
                            )}
                        </div>
                    )}

                    {answer.textAnswer && (
                        <div style={{ marginTop: '1rem' }}>
                            <strong>Your answer:</strong>
                            <p style={{ background: '#f0f4f8', padding: '1rem', borderRadius: '10px', marginTop: '0.5rem' }}>
                                {answer.textAnswer}
                            </p>
                        </div>
                    )}

                    {answer.feedback && (
                        <div className="alert alert-success" style={{ marginTop: '1rem' }}>
                            <strong>💬 Teacher's feedback:</strong> {answer.feedback}
                        </div>
                    )}
                </div>
            ))}

            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <Link to="/" className="btn btn-primary btn-lg">
                    🏠 Back to Home
                </Link>
            </div>
        </div>
    );
}

export default ExamResult;